import { Type as t } from "@sinclair/typebox";
import {
  CategorySchema,
  closed,
  DownloadStageSchema,
  Empty,
  Gid,
  IsoDateTime,
  literals,
  nullable,
  Tag,
  Uuid,
} from "./primitives";
import { route, withIds } from "./route";

export const DownloadOrigin = literals(["web", "bot_read", "bot_search", "rule"] as const);
const count = t.Integer({ minimum: 0 });
export const BotGuild = closed({
  guildId: t.String(),
  note: t.String(),
  addedAt: IsoDateTime,
  commands: closed({
    state: literals(["registered", "failed", "pending"] as const),
    at: nullable(IsoDateTime),
    error: nullable(t.String()),
  }),
});
export const RuleStart = t.Union([
  closed({ mode: t.Literal("date"), date: IsoDateTime }),
  closed({ mode: t.Literal("relative"), days: t.Integer({ minimum: 1, maximum: 3650 }) }),
]);
export const RuleInput = closed({
  name: t.String({ minLength: 1, maxLength: 60 }),
  categories: t.Array(CategorySchema, { uniqueItems: true }),
  artists: t.Array(t.String({ ...Tag, pattern: "^artist:.+" }), { maxItems: 50 }),
  tags: t.Optional(t.Array(Tag, { maxItems: 50, uniqueItems: true })),
  xtags: t.Optional(t.Array(Tag, { maxItems: 50, uniqueItems: true })),
  start: RuleStart,
});
export const DownloadRule = t.Composite(
  [
    RuleInput,
    closed({
      id: Uuid,
      enabled: t.Boolean(),
      effectiveFrom: nullable(IsoDateTime),
      enabledAt: nullable(IsoDateTime),
      phase: literals(["idle", "backfill", "follow"] as const),
      stats: closed({ queued: count, running: count, done: count, failed: count }),
      createdAt: IsoDateTime,
      updatedAt: IsoDateTime,
    }),
  ],
  { additionalProperties: false },
);
export const DownloadOverview = closed({
  slots: closed({ running: count, limit: count }),
  queued: closed({ total: count, byPriority: closed({ read: count, search: count, rule: count }) }),
  last24h: closed({
    done: count,
    failed: count,
    byOrigin: closed({ web: count, botRead: count, botSearch: count, rule: count }),
  }),
});
const shared = { gid: Gid, title: t.String(), expiresAt: IsoDateTime };
const thumb = nullable(t.String({ pattern: "^https://ehgt\\.org/" }));
export const SharePayload = t.Union([
  closed({
    ...shared,
    status: t.Literal("ready"),
    pageCount: count,
    pages: t.Array(
      closed({ index: count, url: t.String(), width: nullable(count), height: nullable(count) }),
    ),
  }),
  closed({
    ...shared,
    status: t.Literal("preparing"),
    pageCount: nullable(count),
    thumb,
    download: closed({
      status: literals(["queued", "running"] as const),
      stage: nullable(DownloadStageSchema),
      progress: closed({ done: count, total: nullable(count) }),
      queuePosition: nullable(count),
    }),
  }),
  closed({ ...shared, status: t.Literal("failed"), thumb }),
]);
export const DiscordInteraction = t.Object(
  {
    type: t.Integer(),
    id: t.String(),
    token: t.String(),
    application_id: t.String(),
    guild_id: t.Optional(t.String()),
    channel: t.Optional(t.Unknown()),
    data: t.Optional(t.Unknown()),
    member: t.Optional(t.Unknown()),
    user: t.Optional(t.Unknown()),
    message: t.Optional(t.Unknown()),
  },
  { additionalProperties: true },
);
export const InteractionResponse = closed({ type: t.Integer(), data: t.Optional(t.Unknown()) });
const guildParams = t.Object({ guildId: t.String({ pattern: "^[0-9]{17,20}$" }) });
const ruleParams = t.Object({ id: Uuid });
export const botRoutes = withIds({
  "bot.interactions": route({
    module: "bot",
    method: "POST",
    path: "/api/discord/interactions",
    access: "discord",
    summary: "Discord 상호작용",
    body: DiscordInteraction,
    responses: { 200: InteractionResponse },
    errors: ["INVALID_SIGNATURE", "UNAVAILABLE"],
  }),
  "bot.share": route({
    module: "bot",
    method: "GET",
    path: "/api/share/:token",
    access: "public",
    summary: "공유 리더",
    params: t.Object({ token: t.String({ minLength: 1, maxLength: 100 }) }),
    responses: { 200: SharePayload },
    errors: ["NOT_FOUND", "POLICY_BLOCKED", "RATE_LIMITED"],
  }),
});
export const botAdminRoutes = withIds({
  "admin.botStatus": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/bot",
    access: "admin",
    summary: "봇 상태",
    responses: {
      200: closed({
        configured: t.Boolean(),
        applicationId: nullable(t.String()),
        guilds: t.Array(BotGuild),
      }),
    },
    errors: [],
  }),
  "admin.addBotGuild": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/bot/guilds",
    access: "admin",
    summary: "길드 추가",
    body: t.Object({ guildId: guildParams.properties.guildId, note: t.String({ maxLength: 100 }) }),
    responses: { 201: BotGuild },
    errors: ["CONFLICT", "UNAVAILABLE"],
  }),
  "admin.removeBotGuild": route({
    module: "admin",
    method: "DELETE",
    path: "/api/admin/bot/guilds/:guildId",
    access: "admin",
    summary: "길드 삭제",
    params: guildParams,
    responses: { 204: Empty },
    errors: ["NOT_FOUND"],
  }),
  "admin.registerBotCommands": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/bot/guilds/:guildId/commands",
    access: "admin",
    summary: "명령 등록",
    params: guildParams,
    responses: { 200: BotGuild },
    errors: ["NOT_FOUND", "UNAVAILABLE"],
  }),
  "admin.listRules": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/download-rules",
    access: "admin",
    summary: "규칙 목록",
    responses: { 200: closed({ items: t.Array(DownloadRule) }) },
    errors: [],
  }),
  "admin.createRule": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/download-rules",
    access: "admin",
    summary: "규칙 생성",
    body: RuleInput,
    responses: { 201: DownloadRule },
    errors: [],
  }),
  "admin.updateRule": route({
    module: "admin",
    method: "PUT",
    path: "/api/admin/download-rules/:id",
    access: "admin",
    summary: "규칙 수정",
    params: ruleParams,
    body: RuleInput,
    responses: { 200: DownloadRule },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
  "admin.setRuleEnabled": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/download-rules/:id/enabled",
    access: "admin",
    summary: "규칙 활성화",
    params: ruleParams,
    body: t.Object({ enabled: t.Boolean() }),
    responses: { 200: DownloadRule },
    errors: ["NOT_FOUND"],
  }),
  "admin.deleteRule": route({
    module: "admin",
    method: "DELETE",
    path: "/api/admin/download-rules/:id",
    access: "admin",
    summary: "규칙 삭제",
    params: ruleParams,
    responses: { 204: Empty },
    errors: ["NOT_FOUND", "CONFLICT"],
  }),
  "admin.cancelRuleQueue": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/download-rules/:id/cancel-queued",
    access: "admin",
    summary: "규칙 대기 취소",
    params: ruleParams,
    responses: { 200: closed({ cancelled: count }) },
    errors: ["NOT_FOUND"],
  }),
  "admin.previewRule": route({
    module: "admin",
    method: "POST",
    path: "/api/admin/download-rules/preview",
    access: "admin",
    summary: "규칙 미리 보기",
    body: t.Composite([RuleInput, closed({ cursor: t.Optional(t.String({ maxLength: 1024 })) })], {
      additionalProperties: false,
    }),
    responses: {
      200: closed({
        matching: nullable(count),
        notDownloaded: nullable(count),
        nextCursor: t.Optional(nullable(t.String())),
      }),
    },
    errors: ["CONFLICT"],
  }),
  "admin.downloadOverview": route({
    module: "admin",
    method: "GET",
    path: "/api/admin/downloads/overview",
    access: "admin",
    summary: "다운로드 개요",
    responses: { 200: DownloadOverview },
    errors: [],
  }),
});
